/**
 * RFQ Responses page — consolidated hub showing all RFQs that need a response.
 *
 * Replaces the old email-by-email review queue. Shows RFQs across all active
 * jobs where a vendor replied but no quotes were extracted (vendor asked
 * questions, sent non-standard response, etc.).
 *
 * Uses the same View/Reply system as the per-job RFQ tab.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchNeedsResponse, dismissResponse } from '../api/rfq';
import { prepareEmailReply } from '../api/email';
import { fetchEmailMessage, fetchEmailAttachments, openAttachment } from '../api/rfq';
import EmailChatModal from '../components/email/EmailChatModal';

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
}

export default function EmailReviewPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Email viewer
  const [viewEmail, setViewEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Reply modal
  const [replyEntryId, setReplyEntryId] = useState(null);
  const [replyLoading, setReplyLoading] = useState(null); // rfq_id being prepared
  const [dismissing, setDismissing] = useState(null); // rfq_id being dismissed

  const loadData = useCallback(async () => {
    try {
      const result = await fetchNeedsResponse();
      setItems(result.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  async function handleViewEmail(emailId) {
    if (!emailId) return;
    setEmailLoading(true);
    try {
      const email = await fetchEmailMessage(emailId);
      let attachments = [];
      try { attachments = await fetchEmailAttachments(emailId); } catch {}
      setViewEmail({ ...email, _emailId: emailId, _attachments: attachments });
    } catch {
      alert('Could not load email.');
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleReply(item) {
    setReplyLoading(item.rfq_id);
    try {
      const result = await prepareEmailReply(item.email_id);
      setReplyEntryId(result.review_queue_id);
    } catch (err) {
      alert('Failed to prepare reply: ' + (err.response?.data?.error || err.message));
    } finally {
      setReplyLoading(null);
    }
  }

  async function handleDismiss(item) {
    setDismissing(item.rfq_id);
    try {
      await dismissResponse(item.email_id);
      // Remove from local list immediately
      setItems(prev => prev.filter(i => i.rfq_id !== item.rfq_id));
    } catch (err) {
      alert('Dismiss failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setDismissing(null);
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-10">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">RFQ Responses Needed</h1>
          <p className="text-sm text-gray-500 mt-1">
            Vendors that replied with questions or non-standard responses across all active jobs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
              {items.length} pending
            </span>
          )}
          <button
            onClick={() => { setLoading(true); loadData(); }}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <div className="text-4xl mb-3">✅</div>
          <h2 className="text-lg font-semibold text-gray-700">All caught up</h2>
          <p className="text-sm text-gray-500 mt-1">No vendor emails need a response right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.rfq_id}
              className="bg-white rounded-lg border border-amber-200 hover:border-amber-300 hover:shadow-sm transition-all"
            >
              <div className="px-4 py-3 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {/* Header row */}
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                      ⚠️ Needs Response
                    </span>
                    <button
                      onClick={() => navigate(`/jobs/${item.job_id}`)}
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      Job #{item.job_number}
                    </button>
                    {item.customer_name && (
                      <span className="text-sm text-gray-400">— {item.customer_name}</span>
                    )}
                    <span className="text-xs text-gray-300">|</span>
                    <span className="text-sm font-medium text-gray-700">{item.vendor_name}</span>
                    <span className="text-xs text-gray-400 ml-auto shrink-0">
                      {fmtDate(item.email_received_at)}
                    </span>
                  </div>

                  {/* Email subject */}
                  <p className="text-sm text-gray-800 font-medium truncate">
                    {item.email_subject || '(no subject)'}
                  </p>

                  {/* Preview */}
                  {item.email_preview && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {item.email_preview}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0 mt-1">
                  <button
                    onClick={() => handleViewEmail(item.email_id)}
                    disabled={emailLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={() => handleReply(item)}
                    disabled={replyLoading === item.rfq_id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {replyLoading === item.rfq_id ? 'Loading...' : 'Reply'}
                  </button>
                  <button
                    onClick={() => handleDismiss(item)}
                    disabled={dismissing === item.rfq_id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                    title="No response needed — dismiss this flag"
                  >
                    {dismissing === item.rfq_id ? '…' : '✕ Dismiss'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email viewer modal */}
      {viewEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setViewEmail(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">{viewEmail.subject || '(no subject)'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">From: {viewEmail.sender || '—'}</p>
              </div>
              <button onClick={() => setViewEmail(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-3">×</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{viewEmail.body_text || '(empty body)'}</pre>
            </div>
            {viewEmail._attachments?.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">Attachments:</span>
                {viewEmail._attachments.map((att, i) => (
                  <button
                    key={i}
                    onClick={() => openAttachment(viewEmail._emailId, att.id, att.filename)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100"
                  >
                    📎 {att.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reply modal */}
      {replyEntryId && (
        <EmailChatModal
          entryId={replyEntryId}
          onClose={() => setReplyEntryId(null)}
          onSent={() => {
            setReplyEntryId(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
